/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Column } from 'primereact/column';
import { PaginatorPageState } from 'primereact/paginator';
import { DataTable, DataTablePFSEvent, DataTableSortOrderType } from 'primereact/datatable';

import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { NotificationLogResponse } from '../../models/notificationLogs/NotificationLogResponse';
import { NotificationLogFilter, KeyValuePair } from '../../models/notificationLogs/NotificationLogFilter';
import { DropdownOption } from '../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../services/httpService';
import { INotifierService, NotifierService } from '../../services/notifierService';
import { nameof } from '../../shared/utility';
import styles from './NotificationLogs.module.scss';

const NotificationLogs = (): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();

	const truncateText = (text: string, maxLength: number): string => {
		return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
	};
	
	const [firstNameFilter, setFirstNameFilter] = useState<string>('');
	const [lastNameFilter, setLastNameFilter] = useState<string>('');
	const [emailFilter, setEmailFilter] = useState<string>('');
	const [divisionFilter, setDivisionFilter] = useState<string | number>('');
	const [yearFilter, setYearFilter] = useState<number | null>(null);
	const [quarterFilter, setQuarterFilter] = useState<number | null>(null);
	const [statusFilter, setStatusFilter] = useState<number | null>(null);

	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>([]);
	const [yearsOptions, setYearsOptions] = useState<DropdownOption[]>([]);
	const [quartersOptions, setQuartersOptions] = useState<DropdownOption[]>([]);
	const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);

	const [notificationLogs, setNotificationLogs] = useState<NotificationLogResponse[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(() => new NotifierService(), []);

	// Debounce timers
	const debounceTimerRef = React.useRef<NodeJS.Timeout>();

	const fetchNotificationLogs = useCallback(() => {
		setLoading(true);

		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}

		const params = new URLSearchParams({
			page: currentPage.toString(),
			size: rows.toString(),
			...(firstNameFilter && { firstName: firstNameFilter }),
			...(lastNameFilter && { lastName: lastNameFilter }),
			...(emailFilter && { email: emailFilter }),
			...(divisionFilter && { division: divisionFilter.toString() }),
			...(yearFilter && { year: yearFilter.toString() }),
			...(quarterFilter && { quarter: quarterFilter.toString() }),
			...(statusFilter && { status: statusFilter.toString() }),
			...(sortField && { sortBy: sortField }),
			...(order && { order: order })
		});

		httpService
			.get<NotificationLogResponse[]>(
				`api/notificationLog?${params.toString()}`,
				undefined
			)
			.then((response) => {
				setNotificationLogs(response);
				setTotalRecords(response.length);
				setLoading(false);
			})
			.catch((err) => {
				notifierService.error();
				setLoading(false);
			});
	}, [
		currentPage,
		rows,
		//firstNameFilter,
		//lastNameFilter,
		emailFilter,
		divisionFilter,
		yearFilter,
		quarterFilter,
		statusFilter,
		sortField,
		sortOrder,
		httpService,
		notifierService
	]);

	// Initial load and filter changes
	useEffect(() => {
		setCurrentPage(1);
		setFirst(0);
	}, [
		//firstNameFilter,
		//lastNameFilter,
		 emailFilter,
		 divisionFilter,
		 yearFilter,
		 quarterFilter,
		 statusFilter
		]);

	useEffect(() => {
		fetchNotificationLogs();
	}, [
		currentPage,
		rows,
		//firstNameFilter,
		//lastNameFilter,
		emailFilter,
		divisionFilter,
		yearFilter,
		quarterFilter,
		statusFilter,
		sortField,
		sortOrder
	]);

	// Fetch filter options
	useEffect(() => {
		httpService
			.get<NotificationLogFilter>('api/notificationLog/filters', undefined)
			.then((filters) => {
				if (filters.divisions) {
					setDivisionsOptions(
						filters.divisions.map((d: string) => ({
							label: d,
							value: d
						}))
					);
				}
				if (filters.years) {
					setYearsOptions(
						filters.years.map((y: number) => ({
							label: y.toString(),
							value: y
						}))
					);
				}
				if (filters.quarters) {
					setQuartersOptions(
						filters.quarters.map((q: KeyValuePair<number, string>) => ({
							label: q.value,
							value: q.key
						}))
					);
				}
				if (filters.status) {
					setStatusOptions(
						filters.status.map((s: KeyValuePair<number, string>) => ({
							label: s.value,
							value: s.key
						}))
					);
				}
			})
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	const handleFirstNameChange = (value: string) => {
		setFirstNameFilter(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
	};

	const handleLastNameChange = (value: string) => {
		setLastNameFilter(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
	};

	const handleEmailChange = (value: string) => {
		setEmailFilter(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	const onRenderSentOn = useCallback(
		(cellData: NotificationLogResponse) => {
			if (!cellData.sentOn) {
				return <span>-</span>;
			}
			try {
				const utcDate = new Date(cellData.sentOn);
				const localDate = new Date(utcDate.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
				const formattedDate = localDate.toLocaleString('en-US', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				});
				return <span>{formattedDate}</span>;
			} catch (error) {
				return <span>{cellData.sentOn?.toString() ?? '-'}</span>;
			}
		},
		[]
	);

	return (
		<>
			<Filters
				divisionsOptions={divisionsOptions}
				yearsOptions={yearsOptions}
				quartersOptions={quartersOptions}
				statusOptions={statusOptions}
				firstNameFilter={firstNameFilter}
				setFirstNameFilter={handleFirstNameChange}
				lastNameFilter={lastNameFilter}
				setLastNameFilter={handleLastNameChange}
				emailFilter={emailFilter}
				setEmailFilter={handleEmailChange}
				divisionFilter={divisionFilter}
				setDivisionFilter={setDivisionFilter}
				yearFilter={yearFilter}
				setYearFilter={setYearFilter}
				quarterFilter={quarterFilter}
				setQuarterFilter={setQuarterFilter}
				statusFilter={statusFilter}
				setStatusFilter={setStatusFilter}
			/>

		<DataTable
			value={notificationLogs}
			responsiveLayout="scroll"
			sortField={sortField}
			sortOrder={sortOrder}
			onSort={onSort}
			loading={loading}
		>
			{/* <Column
				sortable
				header="First Name"
				field={nameof<NotificationLogResponse>('firstName')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Last Name"
				field={nameof<NotificationLogResponse>('lastName')}
				style={{ width: 'fit-content' }}
			/> */}

			<Column
				sortable
				header="Email"
				field={nameof<NotificationLogResponse>('email')}
				style={{ width: '100%' }}
			/>

			<Column
				sortable
				header="Division"
				field={nameof<NotificationLogResponse>('division')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Year"
				field={nameof<NotificationLogResponse>('year')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Quarter"
				field={nameof<NotificationLogResponse>('quarter')}
			/>

			<Column
				sortable
				header="Status"
				field={nameof<NotificationLogResponse>('status')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Email Subject"
				field={nameof<NotificationLogResponse>('emailSubject')}
				style={{ width: '100%' }}
				body={(rowData: NotificationLogResponse) => (
					<span title={rowData.emailSubject.length > 30 ? rowData.emailSubject : ''}>
						{truncateText(rowData.emailSubject, 30)}
					</span>
				)}
			/>

			<Column
				sortable
				header="Sent On (Local Time)"
				field={nameof<NotificationLogResponse>('sentOn')}
				style={{ width: 'fit-content' }}
				body={onRenderSentOn}
			/>
		</DataTable>			<Pagination
				totalRecords={totalRecords}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default NotificationLogs;